import React, { useState } from "react";

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const faqs: FAQItem[] = [
    {
      question: "What is Raah Global?",
      answer:
        "Raah Global is a trusted educational consultancy that helps students from Pakistan and other countries apply for language programs, undergraduate, postgraduate, and short-term study opportunities in South Korea. We provide admission guidance, document preparation, and visa support.",
    },
    {
      question: "Which countries does Raah Global work with?",
      answer:
        "Currently, we specialize in South Korea, but we are also expanding partnerships with institutions in Australia, Malaysia, and other destinations.",
    },
    {
      question: "What services does Raah Global provide?",
      answer: (
        <div>
          <p className="mb-2">We assist with:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>University/Language Program Admission Applications</li>
            <li>Visa Guidance & Document Preparation</li>
            <li>Scholarship & Funding Advice (if available)</li>
            <li>Pre-departure Briefings</li>
            <li>Airport Pickup & Accommodation Support (optional)</li>
          </ul>
        </div>
      ),
    },
    {
      question: "Do I need IELTS or English proficiency to study in Korea?",
      answer:
        "Most Korean language programs do not require IELTS. For degree programs, requirements vary by university, but many offer courses in English where IELTS/TOEFL may be required. We guide students based on their profile.",
    },
    {
      question:
        "How long does it take to get admission in a Korean university?",
      answer:
        "Typically, admission decisions take 2–4 weeks once all documents are submitted. However, timelines may vary depending on the intake and university.",
    },
    {
      question: "What are the intakes for Korean language programs?",
      answer: (
        <div>
          <p className="mb-2">
            Most Korean universities have 4 intakes per year:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>March</strong> (Spring)
            </li>
            <li>
              <strong>June</strong> (Summer)
            </li>
            <li>
              <strong>September</strong> (Fall)
            </li>
            <li>
              <strong>December</strong> (Winter)
            </li>
          </ul>
        </div>
      ),
    },
    {
      question:
        "How much does it cost to study Korean language in South Korea?",
      answer:
        "On average, tuition fees are USD 3,000–5,000 per semester (3 months), excluding accommodation and living expenses. Costs may vary by university.",
    },
    {
      question: "Does Raah Global charge a consultancy fee?",
      answer:
        "Yes, we charge a reasonable service fee for professional admission and visa guidance. Our fees are transparent and communicated clearly before the process starts.",
    },
    {
      question: "Can Raah Global help with visa rejection cases?",
      answer:
        "Yes. If your visa was rejected, we can review your case, identify the reason, and reapply with stronger documentation and strategy.",
    },
    {
      question: "Can I work while studying in South Korea?",
      answer:
        "Yes, international students are allowed to work part-time, with certain conditions and limits on working hours.",
    },
    {
      question: "Why should I choose Raah Global over other agencies?",
      answer: (
        <div>
          <ul className="list-disc list-inside space-y-1">
            <li>We ensure genuine and verified documents (no fake files)</li>
            <li>Direct partnerships with Korean universities</li>
            <li>Professional and transparent process</li>
            <li>High visa success rate</li>
            <li>Personalized student counseling</li>
          </ul>
        </div>
      ),
    },
    {
      question: "How can I contact Raah Global?",
      answer: (
        <div>
          <p className="mb-2">You can reach us via:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Email:</strong> info@raahglobal.com
            </li>
            <li>
              <strong>Website:</strong> www.raahglobal.com
            </li>
            <li>
              <strong>Business Registration:</strong> 815-79-00669
            </li>
            <li>
              <strong>Company:</strong> 라아 글로벌 (Raah Global)
            </li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Everything you need to know about studying in South Korea
          </p>
          <p className="text-sm text-gray-500">
            Can't find what you're looking for? email us at{" "}
            <span className="font-medium text-gray-900">
              info@raahglobal.com
            </span>
            for more information.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <span className="font-medium text-gray-900 pr-4">
                  {faq.question}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 flex-shrink-0 ${
                    openItems.includes(index) ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {openItems.includes(index) && (
                <div className="px-6 pb-4">
                  <div className="border-t border-gray-100 pt-4">
                    <div className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQ;
